import { ReleaseNotesByAppId, ReleaseNote, FeatureReleaseNoteConfig } from "./release-notes-config";
import { CustomKeyValues, TelemetryService } from "../../../ShellCommon/logger/telemetry-service";
import * as ReleaseNotesTelemetryConstants from "../../Constants/ReleaseNotesTelemetryConstants";
import { PreferenceService } from "../preferenceSvc";
import { ReleaseNotesConfigService } from "./release-notes-config";
import { LoggingService } from "../loggingSvc";
import { DialogService } from "../dialog";
import { WindowCommunicationBridgeService } from "../../../ShellCommon/eventModule/window-communication-bridge";
import { FeatureIdentifier } from "../feature-config/feature-config";
import { PreferenceModel } from "../preferenceSvc";

import * as Rx from "@reactivex/rxjs";
import { GlobalTelemetryConstants } from "../../../ShellCommon/logger/telemetry-constants";

export type ReleaseNotesViewStates = { [app: string]: { [featureId: string]: boolean } };
export type ReleaseNotesSetting = { ifShowDetails?: string };
export type ReleasedFeaturesInDate = { date: Date; releaseNotes: ReleaseNotesByAppId };
export type ReleaseFeatureIdentifiersByDate = ReleasedFeaturesInDate[];
export type RollbackFeaturesPerApp = { [app: string]: string[] };
export type UnviewedFeaturesPerApp = {
  [app: string]: {
    [featureId: string]: { hasSummary: boolean; hasFull: boolean; summarySize: number; fullSize: number };
  }[];
};

export class ReleaseNotesService {
  public readonly COMMUNICATION_BRIDGE_FORCE_SPLASH_EVENT_NAME = "SD_FORCE_SPLASH";
  public releaseNoteSplashInProgress = false;
  public MsalInProgress = false;
  public splashNonForceHasBeenViewed = false;
  public showReleaseNoteRollback = false;
  public rollbackFeatures: ReleaseNotesByAppId;
  public appsWithUnviewedFeatures: UnviewedFeaturesPerApp;
  public splashOpenTime: any;
  public splashLoadStart: any;

  private showAsatObservable: Rx.BehaviorSubject<boolean> = new Rx.BehaviorSubject<boolean>(false);
  private showSplashObservable: Rx.BehaviorSubject<boolean> = new Rx.BehaviorSubject<boolean>(true);

  private readonly MONTH_EXPIRATION = 3;
  private readonly SERVICE_DESK_APPID: string = "ServiceDesk";
  private readonly SECTION_ID_RELEASE_NOTE: string = "releaseNoteViewed";
  private readonly SECTION_ID_RELEASE_NOTE_SETTING: string = "releaseNoteSetting";
  private readonly DATE_FORMAT: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", year: "numeric" };

  private releaseNoteViewState: ReleaseNotesViewStates;
  private releasedFeaturesByDate: ReleaseFeatureIdentifiersByDate;
  private releaseNotesSetting: ReleaseNotesSetting;
  private glyphIds: { [appId: string]: string } = {};
  private recentSplashIsForced: boolean;

  private readonly PATH_TO_GLYPH_MAP = "../ServiceDesk/sdServices/release-notes-config/appIdToGlyph.json";

  // @ngInject
  constructor(
    private releaseNotesConfigService: ReleaseNotesConfigService,
    private preferenceService: PreferenceService,
    private loggingService: LoggingService,
    private dialogService: DialogService,
    private windowCommunicationBridgeService: WindowCommunicationBridgeService,
    private $q: ng.IQService,
    private $http: ng.IHttpService,
    private telemetryService: TelemetryService,
    private telemetryConstants: GlobalTelemetryConstants
  ) {
    this.$http.get(this.PATH_TO_GLYPH_MAP).then((appIdToGlyph: any) => {
      if (appIdToGlyph.data) {
        this.glyphIds = appIdToGlyph.data;
      }
    });

    this.windowCommunicationBridgeService.subscribe(
      this.COMMUNICATION_BRIDGE_FORCE_SPLASH_EVENT_NAME,
      () => {
        this.showDialog(true);
      },
      undefined
    );
  }

  public getReleaseNotesToDisplay(force?: boolean): ng.IPromise<ReleaseNotesByAppId> {
    this.recentSplashIsForced = !!force;
    return this.getLatestDayReleaseNotes()
      .then((latestDayOfFeatures: ReleasedFeaturesInDate) => {
        if (latestDayOfFeatures) {
          return latestDayOfFeatures.releaseNotes;
        }

        return {};
      })
      .then((releasedFeatures: ReleaseNotesByAppId) => {
        if (force) {
          return this.$q.resolve(releasedFeatures);
        }
        return this.getReleaseNotesViewedState().then((releaseNoteViewState: ReleaseNotesViewStates) => {
          if (!releaseNoteViewState) {
            // If there is an error getting preferences, do not show anything.
            return;
          }
          const unviewedReleaseNotes: ReleaseNotesByAppId = {};
          let appsWithUnviewedFeatures: UnviewedFeaturesPerApp = {};
          // add all released features that have not been viewed
          Object.keys(releasedFeatures).forEach((appId: string) => {
            releasedFeatures[appId].forEach((releaseNote: ReleaseNote) => {
              if (releaseNoteViewState[appId] && releaseNoteViewState[appId][releaseNote.featureId]) {
                return;
              }
              if (!unviewedReleaseNotes[appId]) {
                unviewedReleaseNotes[appId] = [];
                appsWithUnviewedFeatures[appId] = [];
              }
              unviewedReleaseNotes[appId].push(releaseNote);
              const unviewedFeature = {
                [releaseNote.featureId]: {
                  hasSummary: !!releaseNote.bundleHtmls.splashHtml,
                  hasFull: !!releaseNote.bundleHtmls.fullHtml,
                  summarySize: releaseNote.bundleHtmls.splashHtml ? releaseNote.bundleHtmls.splashHtml.length : 0,
                  fullSize: releaseNote.bundleHtmls.fullHtml ? releaseNote.bundleHtmls.fullHtml.length : 0
                }
              };
              appsWithUnviewedFeatures[appId].push(unviewedFeature);
            });
          });
          this.appsWithUnviewedFeatures = appsWithUnviewedFeatures;
          this.releaseNoteViewState = releaseNoteViewState;
          return unviewedReleaseNotes;
        });
      });
  }

  public isRecentSplashForced(): boolean {
    return this.recentSplashIsForced;
  }

  public cleanUpOldReleaseNotes(): ng.IPromise<void> {
    return this.getFeaturesOlderThanNMonths(this.MONTH_EXPIRATION).then((oldReleaseNotes: ReleaseNotesByAppId) => {
      Object.keys(this.releaseNoteViewState).forEach((appId: string) => {
        Object.keys(this.releaseNoteViewState[appId]).forEach((featureId: FeatureIdentifier) => {
          if (
            oldReleaseNotes[appId] &&
            oldReleaseNotes[appId].find((releaseNote: ReleaseNote) => releaseNote.featureId === featureId)
          ) {
            delete this.releaseNoteViewState[appId][featureId];
          }
        });
      });

      this.saveReleaseNotesViewedState(this.releaseNoteViewState);
    });
  }

  public initReleaseNotes() {
    if (this.splashNonForceHasBeenViewed) {
      return;
    }

    this.splashLoadStart = new Date();
    this.getReleaseNotesSetting()
      .then((setting: ReleaseNotesSetting) => {
        this.releaseNotesSetting = setting && setting.ifShowDetails ? setting : { ifShowDetails: "true" };
        return this.$q.resolve();
      })
      .then(() => {
        this.showDialog();
      });
  }

  /**
   * Shows a dialog with unviewed features (if there are any) and provides callback methods
   * to toggle view detailed notes and close the dialog
   *
   * @param {boolean} force should force dialog open despite preferences
   */
  public showDialog(force?: boolean): void {
    this.getReleaseNotesToDisplay(force)
      .then((releaseNotes: ReleaseNotesByAppId) => {
        if (angular.equals({}, releaseNotes)) {
          return;
        }
        this.getLatestDayReleaseNotes().then((latestDateOfReleases: ReleasedFeaturesInDate) => {
          const dialogContent = `<sd-release-note-splash
            all-unviewed-features=releaseNotes
            original-release-note-view-states=viewState
            date-created=dateCreated
            release-note-setting=setting>
            </sd-release-note-splash>`;

          if (force) {
            this.showSplashDialog(dialogContent, releaseNotes, latestDateOfReleases, force);
          } else {
            this.getAsatObservable().subscribe((isAsatInProgress: boolean) => {
              if (!isAsatInProgress && !this.splashNonForceHasBeenViewed) {
                this.setReleaseNoteSplashObservable(true);
                this.splashNonForceHasBeenViewed = true;
                this.showSplashDialog(dialogContent, releaseNotes, latestDateOfReleases, force);
              }
            });
          }
        });
      })
      .finally(() => {
        this.cleanUpOldReleaseNotes();
      });
  }

  public getAsatObservable(): Rx.BehaviorSubject<boolean> {
    return this.showAsatObservable;
  }

  public setAsatObservable(showAsat: boolean): void {
    this.showAsatObservable.next(showAsat);
  }

  public getReleaseNoteSplashObservable(): Rx.BehaviorSubject<boolean> {
    return this.showSplashObservable;
  }

  public setReleaseNoteSplashObservable(showSplash: boolean): void {
    this.showSplashObservable.next(showSplash);
  }

  public showRollback(): void {
    this.getRollbackFeatures()
      .then((featureList: ReleaseNotesByAppId) => {
        if (!featureList || Object.keys(featureList).length === 0) {
          return;
        }
        return this.releaseNotesConfigService.getReleaseNotesByFeatureList(featureList);
      })
      .then((releaseNotes) => {
        if (releaseNotes) {
          this.shouldShowReleaseNoteRollback(releaseNotes);
        }
      });
  }

  public saveReleaseNotesViewedState(
    appViewState: ReleaseNotesViewStates,
    setting?: ReleaseNotesSetting
  ): ng.IPromise<boolean> {
    let appViewStateUpdate: PreferenceModel = this.generatePreferenceModelForViewState(appViewState);
    let setPreferencePromise: ng.IPromise<Boolean>;
    if (setting) {
      setPreferencePromise = this.preferenceService.setPreference(
        appViewStateUpdate,
        this.generatePreferenceModelForSetting(setting)
      );
    } else {
      setPreferencePromise = this.preferenceService.setPreference(appViewStateUpdate);
    }
    return setPreferencePromise.then((success: boolean) => {
      if (!success) {
        this.loggingService.logError("Unable to save viewed state to preferences service.");
        return false;
      } else {
        return true;
      }
    });
  }

  public getReleaseNotesViewedState(): ng.IPromise<ReleaseNotesViewStates | void> {
    return this.preferenceService
      .getPreference(this.SERVICE_DESK_APPID, this.SECTION_ID_RELEASE_NOTE)
      .then((preferences) => {
        let preferenceContent: { [key: string]: string } = preferences.content;

        if (!preferenceContent) {
          // No preferences exist, show all available released features.
          return {};
        }

        const formattedReleaseNotesViewState: ReleaseNotesViewStates = {};

        Object.keys(preferenceContent).forEach((appId: string) => {
          formattedReleaseNotesViewState[appId] = JSON.parse(preferenceContent[appId]);
        });

        return formattedReleaseNotesViewState;
      })
      .catch((error) => {
        this.loggingService.logError("Unable to get viewed state from preferences service: " + error);
      });
  }

  public getReleaseNotesSetting(): ng.IPromise<ReleaseNotesSetting | void> {
    return this.preferenceService
      .getPreference(this.SERVICE_DESK_APPID, this.SECTION_ID_RELEASE_NOTE_SETTING)
      .then((preferences) => {
        return preferences && preferences.content ? preferences.content : {};
      })
      .catch((error) => {
        this.loggingService.logError("Unable to get release notes settings from preferences service: " + error);
      });
  }

  public getRollbackFeatures(): ng.IPromise<ReleaseNotesByAppId | void> {
    const flattedFeatureList: string[] = [];
    const rollBackFeatures: ReleaseNotesByAppId = {};
    return this.releaseNotesConfigService
      .getFeatureList()
      .then((featureList: FeatureReleaseNoteConfig) => {
        Object.keys(featureList).forEach((appId) => {
          Object.keys(featureList[appId]).forEach((featureId) => {
            // TODO: get releases features in recent 3 months
            flattedFeatureList.push(featureId);
          });
        });
      })
      .then(() => {
        return this.getReleaseNotesViewedState().then((releaseNotesViewState: ReleaseNotesViewStates) => {
          if (releaseNotesViewState) {
            Object.keys(releaseNotesViewState).forEach((appId) => {
              Object.keys(releaseNotesViewState[appId]).forEach((featureId) => {
                if (releaseNotesViewState[appId][featureId] && flattedFeatureList.indexOf(featureId) < 0) {
                  if (!rollBackFeatures[appId]) {
                    rollBackFeatures[appId] = [];
                  }
                  rollBackFeatures[appId].push({ featureId: featureId, bundleHtmls: {} });
                }
              });
            });
            return rollBackFeatures;
          }
        });
      })
      .catch((error: any) => {
        this.loggingService.logError("Unable to get rollbackReleaseNotes: " + error);
      });
  }

  public saveRollbackViewedStatus(viewedRollbackFeatures: ReleaseNotesByAppId): ng.IPromise<boolean> {
    let flattedFeatureList: string[] = [];
    Object.keys(viewedRollbackFeatures).forEach((appId) => {
      viewedRollbackFeatures[appId].forEach((releaseNote) => {
        flattedFeatureList.push(releaseNote.featureId);
      });
    });
    Object.keys(this.releaseNoteViewState).forEach((app) => {
      Object.keys(this.releaseNoteViewState[app]).forEach((featureId) => {
        if (flattedFeatureList.indexOf(featureId) > -1) {
          this.releaseNoteViewState[app][featureId] = false;
        }
      });
    });
    return this.saveReleaseNotesViewedState(this.releaseNoteViewState);
  }

  public getAllActiveReleasedFeatureIdentifiersByDate(): ng.IPromise<ReleaseFeatureIdentifiersByDate> {
    const nMonthsAgo: Date = new Date(Date.now());
    nMonthsAgo.setMonth(nMonthsAgo.getMonth() - this.MONTH_EXPIRATION);
    return this.getAllReleasedFeatureIdentifiersByDate().then((releasedFeatures: ReleasedFeaturesInDate[]) => {
      return releasedFeatures.filter((releasedFeature: ReleasedFeaturesInDate) => releasedFeature.date >= nMonthsAgo);
    });
  }

  public getLatestDayReleaseNotes(): ng.IPromise<ReleasedFeaturesInDate | void> {
    return this.getAllActiveReleasedFeatureIdentifiersByDate().then((releasedFeatures: ReleasedFeaturesInDate[]) =>
      releasedFeatures.length > 0 ? releasedFeatures[0] : undefined
    );
  }

  public getFeaturesOlderThanNMonths(n: number): ng.IPromise<ReleaseNotesByAppId> {
    const nMonthsAgo: Date = new Date(Date.now());
    nMonthsAgo.setMonth(nMonthsAgo.getMonth() - n);

    return this.getAllReleasedFeatureIdentifiersByDate().then((releasedFeatures: ReleasedFeaturesInDate[]) => {
      const oldFeatures: ReleaseNotesByAppId = {};

      releasedFeatures
        .filter((releasedFeatures: ReleasedFeaturesInDate) => releasedFeatures.date < nMonthsAgo)
        .forEach((oldReleasedFeatures: ReleasedFeaturesInDate) => {
          Object.keys(oldReleasedFeatures.releaseNotes).forEach((appId: string) => {
            if (!oldFeatures[appId]) {
              oldFeatures[appId] = [];
            }

            oldFeatures[appId] = oldFeatures[appId].concat(oldReleasedFeatures.releaseNotes[appId]);
          });
        });

      return oldFeatures;
    });
  }

  /**
   * Gets all released features, sorted by release date
   *
   * Example Return:
   * [
   *   { date: new Date("11/12/2018"), releaseNotes: { "HOME": [ "HOME_FEATURE" ] },
   *   { date: new Date("11/11/2018"), releaseNotes: { "CASE": [ "CASE_FEATURE" ], "HOME": [ "HOME_FEATURE_2", "HOME_FEATURE_3" ] }
   * ]
   *
   * @returns {Date} released feature identifiers sorted by date
   */
  private getAllReleasedFeatureIdentifiersByDate(): ng.IPromise<ReleaseFeatureIdentifiersByDate> {
    if (this.releasedFeaturesByDate) {
      return this.$q.resolve(this.releasedFeaturesByDate);
    }

    return this.releaseNotesConfigService
      .getReleasedFeatureIdentifiers()
      .then((currentReleasedFeatures: ReleaseNotesByAppId) => {
        const releaseNotesByDate: ReleaseFeatureIdentifiersByDate = [];

        return this.$q
          .when(
            Object.keys(currentReleasedFeatures).forEach((appId: string) => {
              currentReleasedFeatures[appId].forEach((releaseNote: ReleaseNote) => {
                this.releaseNotesConfigService.getFeatureList().then((featureList: FeatureReleaseNoteConfig) => {
                  if (!featureList[appId] || !featureList[appId][releaseNote.featureId]) {
                    return;
                  }
                  const featureDate = featureList[appId][releaseNote.featureId];
                  const existingDateIndex = releaseNotesByDate.findIndex((releaseNotes: ReleasedFeaturesInDate) => {
                    return releaseNotes.date.getTime() === featureDate.getTime();
                  });
                  if (existingDateIndex === -1) {
                    // Date does not exist
                    const releaseNotes: ReleaseNotesByAppId = {};
                    releaseNotes[appId] = [releaseNote];
                    releaseNotesByDate.push({ date: featureDate, releaseNotes });
                  } else {
                    if (!releaseNotesByDate[existingDateIndex].releaseNotes[appId]) {
                      releaseNotesByDate[existingDateIndex].releaseNotes[appId] = [];
                    }
                    releaseNotesByDate[existingDateIndex].releaseNotes[appId].push(releaseNote);
                  }
                });
              });
            })
          )
          .then(() => {
            // sort release notes by date
            releaseNotesByDate.sort(
              (a: ReleasedFeaturesInDate, b: ReleasedFeaturesInDate) => b.date.getTime() - a.date.getTime()
            );

            this.releasedFeaturesByDate = releaseNotesByDate;
            return releaseNotesByDate;
          });
      });
  }

  private showSplashDialog(
    dialogContent: string,
    releaseNotes: ReleaseNotesByAppId,
    latestDateOfReleases: ReleasedFeaturesInDate,
    force: boolean
  ): void {
    this.dialogService.showContent(
      dialogContent,
      "What's New in Service Desk", // TODO localize
      {
        releaseNotes,
        viewState: this.releaseNoteViewState,
        dateCreated: latestDateOfReleases.date,
        setting: this.releaseNotesSetting
      },
      true
    );
    if (!force) {
      const splashLoadEnd: any = new Date();
      const splashLoadDuration = splashLoadEnd - this.splashLoadStart;
      const splashLoadDurationSeconds = splashLoadDuration / 1000;

      let metaTags: CustomKeyValues = {};
      metaTags[this.telemetryConstants.pageMetadataTags.scenario] =
        ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_APP_ID +
        "." +
        ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_SCENARIO.SPLASH;
      metaTags[this.telemetryConstants.pageMetadataTags.scenarioStep] =
        ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_APP_ID +
        "." +
        ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_SCENARIO.SPLASH +
        "." +
        ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_SCENARIO_STEP.SPLASH_LOADED;

      let data: CustomKeyValues = {};
      this.splashOpenTime = new Date();
      data["unviewedFeaturesPerApp"] = JSON.stringify(this.appsWithUnviewedFeatures);
      data["splashSuccessOpenTime"] = String(this.splashOpenTime);
      data["splashLoadDurationSeconds"] = String(splashLoadDurationSeconds);

      this.telemetryService.pushPageActionEvent(
        this.telemetryConstants.pageActionType.auto,
        this.telemetryConstants.pageActionBehavior.Show,
        metaTags,
        data
      );
    }
  }

  private shouldShowReleaseNoteRollback(rollbackFeatures: ReleaseNotesByAppId): void {
    this.rollbackFeatures = rollbackFeatures;

    let appsWithRollBackFeatures: RollbackFeaturesPerApp = {};
    Object.keys(rollbackFeatures).forEach((appId: string) => {
      rollbackFeatures[appId].forEach((releaseNote: ReleaseNote) => {
        if (!appsWithRollBackFeatures[appId]) {
          appsWithRollBackFeatures[appId] = [];
        }
        appsWithRollBackFeatures[appId].push(releaseNote.featureId);
      });
    });
    const metaTags: CustomKeyValues = {};
    metaTags[this.telemetryConstants.pageMetadataTags.scenario] =
      ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_APP_ID +
      "." +
      ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_SCENARIO.ROLLBACK;
    metaTags[this.telemetryConstants.pageMetadataTags.scenarioStep] =
      ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_APP_ID +
      "." +
      ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_SCENARIO.ROLLBACK +
      "." +
      ReleaseNotesTelemetryConstants.RELEASE_NOTES_TELEMETRY_SCENARIO_STEP.SHOW_ROLLBACK;
    let data: CustomKeyValues = {};
    data["appsWithRollBackFeatures"] = JSON.stringify(appsWithRollBackFeatures);
    data["showRollBackViewTime"] = String(new Date());

    this.telemetryService.pushPageActionEvent(
      this.telemetryConstants.pageActionType.auto,
      this.telemetryConstants.pageActionBehavior.Show,
      metaTags,
      data
    );
    this.showReleaseNoteRollback = true;
  }

  private generatePreferenceModelForViewState(contentToSave: ReleaseNotesViewStates): PreferenceModel {
    const preferenceModel: PreferenceModel = {
      appId: this.SERVICE_DESK_APPID,
      content: {},
      sectionId: this.SECTION_ID_RELEASE_NOTE
    };

    Object.keys(contentToSave).forEach((appName: string) => {
      preferenceModel.content[appName] = angular.toJson(contentToSave[appName]);
    });
    return preferenceModel;
  }

  public getGlyphFromAppId(appId: string): string {
    if (!this.glyphIds[appId]) {
      this.loggingService.logWarning(`AppId, ${appId}, does not exist in glyphIds map: ${Object.keys(this.glyphIds)}`);
    }
    return this.glyphIds[appId];
  }

  private generatePreferenceModelForSetting(contentToSave: ReleaseNotesSetting): PreferenceModel {
    const preferenceModel: PreferenceModel = {
      appId: this.SERVICE_DESK_APPID,
      content: contentToSave,
      sectionId: this.SECTION_ID_RELEASE_NOTE_SETTING
    };
    return preferenceModel;
  }
}
